import { useCallback, useEffect, useState } from "react";

// Per-browser preferences (not synced to Firestore). Stored under one key.
const STORAGE_KEY = "wm.localPrefs";
const EVENT = "wm-local-prefs";

export const DEFAULT_PREFS = {
  coversShowNew: true, // show the "New" section on the Covers page
  coversNewHours: 24, // how recently a session must have been marked Unconfirmed to count as new
  coversGroupBy: "tutor", // "tutor" = one block per rostered tutor; "time" = back-to-back regardless of tutor
};

export const readPrefs = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return { ...DEFAULT_PREFS, ...(raw ? JSON.parse(raw) : {}) };
  } catch {
    return { ...DEFAULT_PREFS };
  }
};

export const writePrefs = (patch) => {
  const next = { ...readPrefs(), ...patch };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable; keep in memory only */
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: next }));
  return next;
};

/** React hook: [prefs, setPrefs(patch)] that stays in sync across components and tabs. */
export const useLocalPrefs = () => {
  const [prefs, setPrefs] = useState(readPrefs);

  useEffect(() => {
    const onLocal = (e) => setPrefs(e.detail);
    const onStorage = (e) => e.key === STORAGE_KEY && setPrefs(readPrefs());
    window.addEventListener(EVENT, onLocal);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT, onLocal);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const update = useCallback((patch) => setPrefs(writePrefs(patch)), []);
  return [prefs, update];
};
